import React, { useEffect } from 'react';
import { Form as FormikForm, useFormikContext } from 'formik';
import { Popup } from 'semantic-ui-react';

import Field from 'legacy/common/components/wysiwyg-form/Field';
import { TierDto } from 'legacy/events/types';
import ToggleField from 'legacy/common/components/wysiwyg-form/ToggleField';
import { UserMe } from 'legacy/user/types';
import { CURRENCY_CODES } from 'legacy/util/constants';
import TicketQuestions from '../../../TicketQuestions';
import { GoToStepFn } from '../types';
import AdditionalOptions from './AdditionalOptions';
import TierType from './TierType';

import styles from '../../FormStyles.module.scss';

type FormProps = {
  ticket?: TierDto;
  user: UserMe;
  isEditing: boolean;
  goToStep: GoToStepFn;
  setIsDirty: (dirty: boolean) => void;
};

const Form: React.FC<FormProps> = ({
  user,
  isEditing,
  ticket,
  goToStep,
  setIsDirty
}) => {
  const { values, dirty } = useFormikContext<any>();
  const isUnsaved = (field: string) =>
    dirty && values[field] !== ticket?.[field];

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty]);

  return (
    <FormikForm>
      <TierType disabled={isEditing} />
      <div>
        <Field
          name="name"
          id="name"
          type="text"
          label={`Name* ${isUnsaved('name') ? '(unsaved)' : ''}`}
          disabled={!values.canDelete}
          placeholder="e.g. Party people VIP package"
        />
        <Field
          type="text"
          id="description"
          name="description"
          label={`Description* ${isUnsaved('description') ? '(unsaved)' : ''}`}
          placeholder="e.g. Bottle service and bounce merch"
        />
      </div>

      <div className="flex gap-6">
        <Field
          id="maxAttendance"
          name="maxAttendance"
          type="number"
          label={`Available tickets* ${
            isUnsaved('maxAttendance') ? '(unsaved)' : ''
          }`}
          placeholder="e.g. 50"
        />
        {!values.canDelete && (
          <span className={styles.tooltip}>
            <Popup
              hoverable
              position="right center"
              trigger={<i className="fa fa-info-circle" />}
              size="tiny"
              content="Price cannot be edited after guests have bought a ticket"
            />
          </span>
        )}

        <Field
          id="price"
          name="price"
          type="number"
          disabled={!values.canDelete}
          label={`Price (${CURRENCY_CODES[user?.country]})* ${
            isUnsaved('price') ? '(unsaved)' : ''
          }`}
          placeholder="e.g. $50"
        />
      </div>

      <ToggleField name="requiresApproval" label="Require approval" />
      {values.requiresApproval && <TicketQuestions name="questions" />}

      <AdditionalOptions
        isEditing={isEditing}
        ticket={ticket}
        goToStep={goToStep}
        isUnsaved={isUnsaved}
      />
    </FormikForm>
  );
};

export default Form;
